import { useCallback, useEffect, useState } from 'react'

import { TradingAccountsAdmin } from '@/components/TradingAccountsAdmin'
import { Card, Notice } from '@/components/ui'
import { api } from '@/lib/api'
import type {
  BrokerAccountOption,
  CTraderStatus,
  ManagedUser,
  TradingAccountAdmin,
  UpdateUserBody,
  UserRole,
} from '@/lib/types'

/**
 * Administration : traders, comptes de trading, raccordement cTrader.
 *
 * Un trader peut désormais piloter PLUSIEURS comptes — un compte Capital.com,
 * un compte de challenge FTMO — et choisit lequel il a sous les yeux depuis
 * le bandeau du haut. On lui ouvre des comptes en les cochant : c'est ce lien
 * qui cloisonne l'outil, il ne voit que les comptes cochés.
 */
export function AdminPanel({
  currentUsername,
  ctraderReturn = null,
  onAccountsChanged,
}: {
  currentUsername: string | null
  ctraderReturn?: { ok: boolean; detail: string } | null
  onAccountsChanged?: () => void
}) {
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [brokerAccounts, setBrokerAccounts] = useState<BrokerAccountOption[]>([])
  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountAdmin[]>([])
  const [ctrader, setCtrader] = useState<CTraderStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(
    ctraderReturn?.ok ? `cTrader raccordé : ${ctraderReturn.detail}.` : null,
  )
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const [liste, capital, comptes, statut] = await Promise.all([
        api.users(),
        api.brokerAccounts(),
        api.tradingAccounts(),
        api.ctraderStatus(),
      ])
      setUsers(liste)
      setBrokerAccounts(capital)
      setTradingAccounts(comptes)
      setCtrader(statut)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const changed = useCallback(() => {
    void load()
    // Le sélecteur du bandeau liste aussi ces comptes : il doit suivre.
    onAccountsChanged?.()
  }, [load, onAccountsChanged])

  async function patch(user: ManagedUser, body: UpdateUserBody, message: string) {
    setBusy(true)
    setError(null)
    try {
      await api.updateUser(user.id, body)
      setNotice(message)
      changed()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Modification refusée')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {ctraderReturn && !ctraderReturn.ok && (
        <Notice
          tone="error"
          title="Raccordement cTrader"
          items={[ctraderReturn.detail || 'Autorisation refusée.']}
        />
      )}
      {error && <Notice tone="error" title="Administration" items={[error]} />}
      {notice && <Notice tone="warn" title="Fait" items={[notice]} />}

      <TradingAccountsAdmin
        accounts={tradingAccounts}
        brokerAccounts={brokerAccounts}
        ctrader={ctrader}
        onChanged={changed}
        onError={setError}
        onNotice={setNotice}
      />

      <CreateUserForm
        accounts={tradingAccounts}
        onCreated={(message) => {
          setNotice(message)
          changed()
        }}
        onError={setError}
      />

      <Card title={`Traders (${loaded ? users.length : '…'})`}>
        {!loaded ? (
          <p className="px-3 py-6 text-center text-[13px] text-ink-muted">Chargement…</p>
        ) : (
          <ul className="divide-y divide-border">
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                accounts={tradingAccounts}
                isSelf={user.username === currentUsername}
                busy={busy}
                onPatch={patch}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

/**
 * Cases à cocher des comptes ouverts à un utilisateur.
 *
 * Une case par compte, avec son courtier et son caractère réel ou démo : un
 * compte FTMO et un compte Capital.com de même nom ne doivent pas pouvoir se
 * confondre au moment de les ouvrir à quelqu'un.
 */
function AccountChecklist({
  accounts,
  selected,
  onToggle,
  disabled = false,
}: {
  accounts: TradingAccountAdmin[]
  selected: number[]
  onToggle: (id: number) => void
  disabled?: boolean
}) {
  if (accounts.length === 0) {
    return (
      <p className="text-[11px] text-ink-muted">
        Aucun compte de trading : ajoutez-en un plus haut.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      {accounts.map((account) => (
        <label
          key={account.id}
          className="flex min-h-touch cursor-pointer items-center gap-2 rounded-md border border-border bg-elevated/40 px-2"
        >
          <input
            type="checkbox"
            checked={selected.includes(account.id)}
            disabled={disabled}
            onChange={() => onToggle(account.id)}
            className="h-4 w-4 accent-[#2962ff]"
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] text-ink">{account.display_name}</span>
            <span className="num block truncate text-[10px] text-ink-muted">
              {account.broker_name || account.provider} · n° {account.external_id}
              {account.is_live ? ' · RÉEL' : ' · démo'}
            </span>
          </span>
        </label>
      ))}
    </div>
  )
}

function CreateUserForm({
  accounts,
  onCreated,
  onError,
}: {
  accounts: TradingAccountAdmin[]
  onCreated: (message: string) => void
  onError: (message: string) => void
}) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<UserRole>('trader')
  const [selected, setSelected] = useState<number[]>([])
  const [busy, setBusy] = useState(false)

  const tooShort = password.length > 0 && password.length < 8

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!username || password.length < 8 || busy) return
    setBusy(true)
    try {
      await api.createUser({ username, password, role, account_ids: selected })
      onCreated(`Compte « ${username.toLowerCase()} » créé.`)
      setUsername('')
      setPassword('')
      setSelected([])
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Création refusée')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Nouveau trader">
      <form onSubmit={submit} className="flex flex-col gap-3 p-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-muted">
              Identifiant
            </span>
            <input
              type="text"
              value={username}
              // iOS capitalise la première lettre : le trader taperait « Paul »
              // là où le compte s'appelle « paul ». Le serveur compare en
              // minuscules, mais autant ne pas créer la confusion.
              autoCapitalize="none"
              autoCorrect="off"
              onChange={(event) => setUsername(event.target.value)}
              className="min-h-touch w-full rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-muted">
              Mot de passe
            </span>
            <input
              type="text"
              value={password}
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
              className={`min-h-touch w-full rounded-md border bg-elevated px-3 text-ink outline-none focus:border-accent ${
                tooShort ? 'border-down/60' : 'border-border'
              }`}
            />
            {/* En clair, volontairement : c'est vous qui le transmettez au
                trader, et un mot de passe qu'on ne peut pas relire se
                retranscrit de travers une fois sur deux. */}
            <span className="mt-1 block text-[10px] text-ink-muted">
              {tooShort ? '8 caractères minimum' : 'Visible : à transmettre au trader'}
            </span>
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-muted">
            Rôle
          </span>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value as UserRole)}
            className="min-h-touch w-full rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
          >
            <option value="trader">Trader</option>
            <option value="admin">Administrateur</option>
          </select>
        </label>

        <div>
          <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">
            Comptes de trading ouverts
          </p>
          <AccountChecklist
            accounts={accounts}
            selected={selected}
            onToggle={(id) =>
              setSelected((current) =>
                current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
              )
            }
          />
        </div>

        <button
          type="submit"
          disabled={busy || !username || password.length < 8}
          className="min-h-touch rounded-md bg-accent text-[14px] font-semibold text-white disabled:opacity-40"
        >
          {busy ? 'Création…' : 'Créer le compte'}
        </button>
      </form>
    </Card>
  )
}

function UserRow({
  user,
  accounts,
  isSelf,
  busy,
  onPatch,
}: {
  user: ManagedUser
  accounts: TradingAccountAdmin[]
  isSelf: boolean
  busy: boolean
  onPatch: (user: ManagedUser, body: UpdateUserBody, message: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const granted = user.accounts.map((account) => account.id)
  const active = user.accounts.find((account) => account.id === user.active_account_id)

  return (
    <li className="px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-ink-strong">
            {user.username}
            {user.role === 'admin' && (
              <span className="ml-2 rounded bg-accent/20 px-1.5 py-0.5 text-[10px] font-semibold text-accent">
                ADMIN
              </span>
            )}
            {!user.is_active && (
              <span className="ml-2 rounded bg-down/20 px-1.5 py-0.5 text-[10px] font-semibold text-down">
                DÉSACTIVÉ
              </span>
            )}
          </p>
          <p className="num truncate text-[11px] text-ink-muted">
            {user.accounts.length === 0
              ? 'aucun compte de trading'
              : `${user.accounts.length} compte${user.accounts.length > 1 ? 's' : ''}` +
                (active ? ` · actif : ${active.display_name}` : '')}
            {user.last_login_at
              ? ` · vu le ${new Date(user.last_login_at).toLocaleDateString('fr-FR')}`
              : ' · jamais connecté'}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="min-h-touch shrink-0 rounded border border-border px-3 text-[12px] text-ink-muted hover:bg-elevated/70 active:bg-elevated"
        >
          {open ? 'Fermer' : 'Modifier'}
        </button>
      </div>

      {open && (
        <div className="mt-2 flex flex-col gap-2 rounded-md border border-border bg-elevated/40 p-2">
          <div>
            <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">
              Comptes ouverts
            </p>
            <AccountChecklist
              accounts={accounts}
              selected={granted}
              disabled={busy}
              onToggle={(id) => {
                const next = granted.includes(id)
                  ? granted.filter((item) => item !== id)
                  : [...granted, id]
                onPatch(user, { account_ids: next }, `Comptes de ${user.username} mis à jour.`)
              }}
            />
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-[180px] flex-1">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-muted">
                Nouveau mot de passe
              </span>
              <input
                type="text"
                value={password}
                autoComplete="new-password"
                onChange={(event) => setPassword(event.target.value)}
                className="min-h-touch w-full rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
              />
            </label>
            <button
              type="button"
              disabled={busy || password.length < 8}
              onClick={() => {
                onPatch(user, { password }, `Mot de passe de ${user.username} remplacé.`)
                setPassword('')
              }}
              className="min-h-touch rounded-md border border-border bg-elevated px-3 text-[12px] text-ink hover:bg-border/60 disabled:opacity-40"
            >
              Remplacer
            </button>
          </div>

          {/*
            On ne se désactive pas soi-même : le serveur le refuse déjà, mais
            un bouton qui échoue toujours est un mauvais bouton.
          */}
          {!isSelf && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                onPatch(
                  user,
                  { is_active: !user.is_active },
                  user.is_active
                    ? `${user.username} désactivé : il ne peut plus se connecter.`
                    : `${user.username} réactivé.`,
                )
              }
              className={`min-h-touch rounded-md border px-3 text-[12px] font-semibold disabled:opacity-40 ${
                user.is_active
                  ? 'border-down/50 text-down active:bg-down/10'
                  : 'border-up/50 text-up active:bg-up/10'
              }`}
            >
              {user.is_active ? 'Désactiver ce compte' : 'Réactiver ce compte'}
            </button>
          )}
        </div>
      )}
    </li>
  )
}
